export type RecoveryChannel = {
  id: string
  name: string
  iconUrl?: string
}

const manymoonsMark = 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'

// Discreet selling: a shushing face, drawn inline so it inherits the icon
// treatment used by the marketplace logos.
export const shushMark =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 1.5A10.5 10.5 0 1 0 22.5 12 10.51 10.51 0 0 0 12 1.5Zm0 2.2a8.3 8.3 0 1 1-8.3 8.3A8.31 8.31 0 0 1 12 3.7Z'/%3E%3Ccircle cx='8.9' cy='10' r='1.25'/%3E%3Ccircle cx='15.1' cy='10' r='1.25'/%3E%3Crect x='11.05' y='12.1' width='1.9' height='6.4' rx='.95'/%3E%3Crect x='6.6' y='14.3' width='3.6' height='1.7' rx='.85'/%3E%3C/svg%3E"

export const recoveryChannels: RecoveryChannel[] = [
  { id: 'manymoons', name: 'Manymoons', iconUrl: manymoonsMark },
  { id: 'prive', name: 'Privé', iconUrl: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg' },
  { id: 'tiktok', name: 'Live Selling', iconUrl: manymoonsMark },
  { id: 'whatnot', name: 'Discreet', iconUrl: shushMark },
  { id: 'ebay', name: 'eBay', iconUrl: 'https://cdn.magicpatterns.com/uploads/7ctVShw3HBQcunanqiLN75/ebay.svg' },
  { id: 'poshmark', name: 'Poshmark', iconUrl: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg' },
  { id: 'vinted', name: 'Vinted', iconUrl: 'https://cdn.magicpatterns.com/uploads/6n8bfmq2ixHXbjbeTaLCJz/vinted.svg' },
  { id: 'vestiaire', name: 'Vestiaire', iconUrl: 'https://cdn.magicpatterns.com/uploads/tLawC51vDdyw6q2RLyZ5Bc/vestiaire.svg' },
  { id: 'depop', name: 'Depop', iconUrl: 'https://cdn.magicpatterns.com/uploads/fG9XmMcsDQikNiC1uba9MJ/dopop.svg' },
  { id: 'mercari', name: 'Mercari', iconUrl: 'https://cdn.magicpatterns.com/uploads/cisCD3tYpjAhP5v7GoDZxD/mercari.svg' },
]

export const recoveryMarkets = ['North America', 'Latin America', 'Europe', 'Middle East', 'Africa', 'Asia', 'Oceania']

export type RecoveryRules = {
  channels: string[]
  markets: string[]
  indexPrice: number
  // The negotiated discount ceiling. While locked, the pricing limit is pinned here.
  indexLocked: boolean
  liveSellingAfterMonth: number
}

// The account-level defaults set in Settings. Every submission inherits these
// until someone overrides them for that specific upload.
export const defaultRecoveryRules: RecoveryRules = {
  channels: ['manymoons', 'ebay', 'whatnot'],
  markets: [...recoveryMarkets],
  indexPrice: 50,
  indexLocked: true,
  liveSellingAfterMonth: 3,
}

// Brands can carry their own negotiated starting point; everything else falls
// back to the account defaults above.
const brandOverrides: Record<string, Partial<RecoveryRules>> = {
  Nisolo: {
    indexPrice: 70,
    channels: ['manymoons', 'whatnot'],
    markets: ['North America', 'Europe', 'Asia', 'Oceania'],
  },
}

// Individual uploads can depart from their brand's footprint — damaged goods,
// for example, only move through live selling and discreet channels.
const submissionOverrides: Record<string, Partial<RecoveryRules>> = {
  '472 — Imperfects': { channels: ['manymoons', 'prive', 'tiktok', 'whatnot'] },
  '473 — Damages': { channels: ['tiktok', 'whatnot'] },
}

export function defaultRulesForBrand(brand?: string, batchName?: string): RecoveryRules {
  return {
    ...defaultRecoveryRules,
    ...(brand ? brandOverrides[brand] ?? {} : {}),
    ...(batchName ? submissionOverrides[batchName] ?? {} : {}),
  }
}

export function rulesMatchDefaults(rules: RecoveryRules, brand?: string, batchName?: string) {
  const base = defaultRulesForBrand(brand, batchName)
  return (
    rules.indexPrice === base.indexPrice &&
    rules.indexLocked === base.indexLocked &&
    rules.liveSellingAfterMonth === base.liveSellingAfterMonth &&
    rules.channels.length === base.channels.length &&
    rules.channels.every((id) => base.channels.includes(id)) &&
    rules.markets.length === base.markets.length &&
    rules.markets.every((market) => base.markets.includes(market))
  )
}

export function summarizeRules(rules: RecoveryRules) {
  return `${rules.channels.length} ${rules.channels.length === 1 ? 'channel' : 'channels'} · ${rules.markets.length} ${rules.markets.length === 1 ? 'market' : 'markets'} · ${rules.indexPrice}% off MSRP`
}
